import React from 'react'
import Typography from '@mui/material/Typography'
import { Grid, Modal } from '@mui/material'
import { Strings } from '../../Strings'
import { checkNull } from '../../Helpers'
import AautiPayLoader from '../AautiPayLoader'

/**
 * Converts a raw API status string to the status format expected by PaymentStatus.
 * Use this when passing status from pay-response or subscription APIs directly.
 *
 * @param {string} apiStatus - Raw status string from the API (e.g., "failed", "subscription created")
 * @returns {string} Normalized status string for PaymentStatus component
 */
export const normalizeApiStatus = (apiStatus) => {
  if (!apiStatus || typeof apiStatus !== 'string') return Strings.fail
  const statusLower = apiStatus.toLowerCase()
  const statusMap = {
    'success': 'success',
    'failed': 'fail',
    'pending': 'pending',
    'initialized': 'pending',
    'subscription created': Strings.subscriptionSuccess,
    'subscription updated': Strings.updatePayMethod,
    'subscription failed': 'fail',
    'subscription pending': Strings.subPending,
    'subscription amount paid': Strings.subscriptionCharged,
    'subscription amount failed': 'fail',
    'subscription paused': 'done',
    'subscription deleted or cancelled': 'done',
    'subscription completed': 'done',
    'subscription resumed': Strings.subscriptionSuccess,
    'refunded': 'done',
    'refund failed': 'fail',
  }
  return statusMap[statusLower] || apiStatus
}

/**
 * Renders a component to display payment status information.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.status - The payment status (e.g., "success", "fail").
 * @param {string} props.message - The message associated with the payment status.
 * @param {boolean} props.sm - A flag indicating whether to display the component in a small size.
 * @returns {JSX.Element} The rendered PaymentStatus component.
 */
const PaymentStatus = (props) => {
  const { status, message, sm, primaryColor, loaderColor, onFocusPopup, hasPopup, onRetry, onGoBack } = props
  const isLoading = status === 'loading' || status === 'url'
  const payFail = status === 'fail'
  const paySuccessFull = status === 'success'
  const isWait = status === Strings.wait
  const requiresAction = status == Strings.requires_action
  const cancel = status === Strings.cancel
  const subscriptionSuccess = status === Strings.subscriptionSuccess
  const updatePayMethod = status === Strings.updatePayMethod
  const updateFailed = status === Strings.updateFailed
  const subPending = status === Strings.subPending
  const payPending = status === Strings.payPending || status === "pending"
  const isDone = status === 'done'

  let pending = requiresAction || subPending || payPending || isWait

  const themeColor = primaryColor || '#0068EF'

  return (
    <>
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {isLoading ? (
          <div
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              padding: '40px 24px',
            }}
          >
            <AautiPayLoader
              color={loaderColor || themeColor}
              text="Processing Payment"
              subText="Please be patient, this process might take some time.<br/>Do not refresh or close this window."
            >
              {hasPopup && onFocusPopup && (
                <Typography
                  onClick={onFocusPopup}
                  sx={{
                    mt: 3,
                    fontSize: '13px',
                    color: themeColor,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                    <rect x="1" y="3" width="9" height="8" rx="1.5" stroke={themeColor} strokeWidth="1.3" fill="none" />
                    <path d="M5 3V2a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1h-1" stroke={themeColor} strokeWidth="1.3" fill="none" />
                  </svg>
                  Go to payment page
                </Typography>
              )}
            </AautiPayLoader>
          </div>
        ) : (
          <div
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              padding: '40px 24px',
            }}
          >
            {/* Status icon with background circle */}
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 22,
                background:
                  paySuccessFull || subscriptionSuccess || updatePayMethod || isDone
                    ? 'linear-gradient(135deg, #e8f5e9, #c8e6c9)'
                    : pending
                      ? 'linear-gradient(135deg, #fff3e0, #ffe0b2)'
                      : 'linear-gradient(135deg, #ffebee, #ffcdd2)',
              }}
            >
              {paySuccessFull || subscriptionSuccess || updatePayMethod || isDone ? (
                <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="16" fill="#43a047" />
                  <path d="M14 20l4 4 8-8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : pending ? (
                <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="16" fill="#fb8c00" />
                  <path d="M20 13v8l5 3" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="16" fill="#e53935" />
                  <path d="M15 15l10 10M25 15l-10 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              )}
            </div>

            {!subscriptionSuccess && !updatePayMethod && (
              <Typography
                fontWeight={500}
                sx={{
                  mb: 1,
                  fontSize: '15px',
                  color: '#1a1a2e',
                  letterSpacing: '-0.3px',
                }}
              >
                {isDone
                  ? `You're all done here`
                  : paySuccessFull
                    ? `Payment Successful!`
                    : updateFailed
                      ? `Update Failed`
                      : isWait
                        ? `Payment in Process`
                        : requiresAction
                          ? ``
                          : subPending
                            ? status
                            : cancel
                              ? `Payment Cancelled`
                              : payFail
                                ? `Payment Failed!`
                                : status ?? `Payment Failed!`}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: '13px',
                textAlign: 'center',
                color: '#8a96a8',
                lineHeight: 1.7,
              }}
            >
              {isDone
                ? `${message || "You've either completed your payment or this checkout session has timed out."}`
                : paySuccessFull
                  ? `Your payment has been processed successfully!`
                  : subscriptionSuccess
                    ? Strings.subscriptionSuccess
                    : updatePayMethod
                      ? checkNull(message)
                        ? Strings.updatePayMethod
                        : message
                      : updateFailed
                        ? `${message || 'Unable to update your payment method. Please try again.'}`
                        : isWait
                          ? `${message ||
                          'Your payment is in process. It will take 4-5 days for completion.'
                          }`
                          : requiresAction
                            ? `${message || Strings.requiresActionMsg}`
                            : payFail
                              ? `${message || 'Please try again or use a different payment method.'}`
                              : subPending
                                ? message
                                : `${message ||
                                'Any amount deducted will be refunded within 7 days.'
                                }`}
            </Typography>

            {/* Retry / Go Back buttons for failed or cancelled payments */}
            {(payFail || cancel) && (onRetry || onGoBack) && (
              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                {onGoBack && (
                  <div
                    onClick={onGoBack}
                    style={{
                      padding: '8px 22px',
                      borderRadius: 8,
                      border: `1px solid #d0d5dd`,
                      color: '#3a3f4b',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      background: '#fff',
                    }}
                  >
                    Go Back
                  </div>
                )}
                {onRetry && (
                  <div
                    onClick={onRetry}
                    style={{
                      padding: '8px 22px',
                      borderRadius: 8,
                      background: themeColor,
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                      opacity: 0.85,
                    }}
                  >
                    Retry
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {status === 'url' && (
        <Modal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 10,
          }}
          open={true}
        >
          <Grid
            container
            style={{
              maxWidth: 650,
              maxHeight: 650,
              minHeight: 400,
              backgroundColor: 'white',
              padding: 10,
              borderRadius: 4,
            }}
          >
            <iframe
              id='myIframe'
              src={message}
              referrerPolicy='unsafe-url'
              style={{ height: 'auto', width: '100%', border: 'none' }}
              title='3D secure'
            ></iframe>
          </Grid>
        </Modal>
      )}
    </>
  )
}

export default PaymentStatus
